import type { CashAccountChoice } from '../api/cash';
import type { CashAccountKind, PaymentMethod } from '../generated/enums';

/**
 * "Paid from" — which account a voucher's money left (Cash & bank step 2,
 * `docs/Cash_Bank_Flow_Design.md` §3.1). The server's `PaidFrom`, mirrored so the picker never
 * offers an account it would refuse.
 *
 * <p>
 * <strong>Notes come out of a till or a petty-cash box; every other method out of a bank.</strong>
 * `null` means "the branch's default for this method", which is what a voucher meant before this
 * step and what an old phone build still sends.
 * </p>
 */

/** What each kind of account is called beside its name. */
export const accountKindWords: Record<CashAccountKind, string> = {
  Till: 'Till',
  Bank: 'Bank',
  PettyCash: 'Petty cash',
};

/** Whether an account of this kind can pay by this method — the server's `PaidFrom.Takes`. */
export function takes(kind: CashAccountKind, method: PaymentMethod): boolean {
  return method === 'Cash' ? kind === 'Till' || kind === 'PettyCash' : kind === 'Bank';
}

/** The accounts a voucher paid this way may name, defaults first, as the server lists them. */
export function accountsFor(
  method: PaymentMethod,
  accounts: readonly CashAccountChoice[] | undefined,
): CashAccountChoice[] {
  return (accounts ?? []).filter((account) => takes(account.kind, method));
}

/**
 * Where a voucher paid this way starts: the default till for cash, the default bank otherwise —
 * `null` when the branch has none yet, which the server reads as "create it on first use".
 */
export function defaultPaidFrom(
  method: PaymentMethod,
  accounts: readonly CashAccountChoice[] | undefined,
): number | null {
  const kind: CashAccountKind = method === 'Cash' ? 'Till' : 'Bank';

  return (accounts ?? []).find((account) => account.kind === kind && account.isDefault)?.id ?? null;
}

/**
 * The account after the method changes: kept while it still takes the new method, the new method's
 * default otherwise — so switching from cash to UPI never leaves petty cash chosen.
 */
export function paidFromAfter(
  chosen: number | null,
  method: PaymentMethod,
  accounts: readonly CashAccountChoice[] | undefined,
): number | null {
  const still = accountsFor(method, accounts).some((account) => account.id === chosen);

  return still ? chosen : defaultPaidFrom(method, accounts);
}

/** Why the chosen account cannot pay this way, or null. Unchosen is never a problem. */
export function paidFromProblem(
  chosen: number | null,
  method: PaymentMethod,
  accounts: readonly CashAccountChoice[] | undefined,
): string | null {
  if (chosen === null) {
    return null;
  }

  const account = (accounts ?? []).find((option) => option.id === chosen);

  if (!account) {
    return 'That account no longer takes money. Choose another.';
  }

  return takes(account.kind, method)
    ? null
    : method === 'Cash'
      ? 'Cash is paid from a till or petty cash.'
      : 'Only a bank account can pay that way.';
}

/** "Petty cash" alone when the name says it, "HDFC · Bank" otherwise. */
export function paidFromLabel(account: CashAccountChoice): string {
  const kind = accountKindWords[account.kind];

  return account.name.trim().toLowerCase() === kind.toLowerCase()
    ? account.name
    : `${account.name} · ${kind}`;
}
